import React, { useState } from "react";
import { DialogContent, Grid, PaperProps } from "@mui/material";
import { useAppSelector } from "../../../../redux/store";
import { BarekamSelect } from "../../../components/select/select";
import { Message, MessageSections } from "../../../components/message/message";
import { BarekamMenuItem, SelectedBarekamMenuItem } from "../../../components/select/styled/orders";
import { Recipient } from "../../../shared/models/user/recipient/recipient";
import { MonthlyLimit } from "../../../shared/models/monthly-limit";
import BarekamInput from "../../../components/input";
import { StandardDialogFooter } from "../../../components/dialogs/standard-dialog/standard-dialog";
import { getMessage } from "../../../shared/helper-functions";
import BarekamText from "../../../components/text";

export const EditRecipientLimits = ({ recipientToEdit, limitSaved, popupClosed }: EditRecipientLimitsProps) => {
    const { allRecipients } = useAppSelector(state => state.recipientDataReducer);
    const { weightUnits, currencies } = useAppSelector(state => state.staticDataReducer);

    const [recipient, setRecipient] = useState<Recipient | undefined>(recipientToEdit);
    const [monthlyLimit, setMonthlyLimit] = useState<MonthlyLimit>(recipientToEdit.monthlyLimit
        ? { ...recipientToEdit.monthlyLimit }
        : {
            weightUnit: weightUnits.find((unit) => unit.weightUnitId === 1) || weightUnits[0],
        }
    );

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const { messageCache, languageId } = useAppSelector(state => state.messagesReducer);

    function handleChange(value: number, fieldName: 'recipient' | 'weightLimitPerParcel' | 'priceLimitPerParcel' | 'pieceCountLimitPerRecipient' | 'currency') {
        setErrors(prevState => {
            delete prevState[fieldName];
            return prevState;
        })

        const modifiedMonthlyLimit: { [key: string]: any } = {
            ...monthlyLimit,
            [fieldName]: fieldName === 'currency' ? currencies.find(cur => cur.currencyId === value) : value
        }

        if (value === 0) {
            delete modifiedMonthlyLimit[fieldName];
        }

        if (fieldName === 'recipient') {
            setRecipient(allRecipients.find(recipient => recipient.recipientId === value));
        }

        setMonthlyLimit(modifiedMonthlyLimit);
    }

    function validate() {
        const errors = {} as { [key: string]: string };

        if (!recipient?.recipientId) {
            errors.recipient = 'required';
        }

        const hasValue = monthlyLimit.weightLimitPerParcel || monthlyLimit.priceLimitPerParcel || monthlyLimit.pieceCountLimitPerRecipient;

        if (!hasValue) {
            errors.weightLimitPerParcel = 'at_least_one_required';
            errors.priceLimitPerParcel = 'at_least_one_required';
            errors.pieceCountLimitPerRecipient = 'at_least_one_required';
        }

        return errors;
    }

    function save() {
        const validationErrors = validate();
        setErrors(validationErrors);

        if (!Object.keys(validationErrors).length && recipient) {
            limitSaved(recipient, monthlyLimit);
        }
    }
    
    return (
        <>
            <DialogContent>
                <Grid container rowSpacing={'1rem'} columnSpacing={'1rem'}>
                    <Grid item xs={12}>
                        <BarekamSelect
                            variant={'outlined'}
                            label={getMessage('recipient', MessageSections.USER, messageCache, languageId)}
                            value={recipient?.recipientId || ''}
                            color={'primary'}
                            onChange={(event) => handleChange(event.target.value as number, 'recipient')}
                            type={"standard"}
                            error={!!errors['recipient']}
                            errorMessage={errors['recipient'] && getMessage(errors['recipient'], MessageSections.USER, messageCache, languageId)}>
                            {allRecipients.map(myRecipient => {
                                if (recipientToEdit?.recipientId === myRecipient.recipientId) {
                                    return (
                                        <SelectedBarekamMenuItem value={myRecipient.recipientId}
                                            key={myRecipient.recipientId}>
                                            {myRecipient.firstName} {myRecipient.lastName}
                                        </SelectedBarekamMenuItem>
                                    )
                                } else {
                                    return (
                                        <BarekamMenuItem value={myRecipient.recipientId}
                                            key={myRecipient.recipientId}>
                                            {myRecipient.firstName} {myRecipient.lastName}
                                        </BarekamMenuItem>
                                    );
                                }
                            })}
                        </BarekamSelect>
                    </Grid>

                    <Grid item xs={4}>
                        <BarekamInput value={monthlyLimit.weightLimitPerParcel}
                            placeholder={getMessage('weight_kg', MessageSections.USER, messageCache, languageId)}
                            label={getMessage('weight_kg', MessageSections.USER, messageCache, languageId)}
                            type={"number"}
                            customInputVariant={"standard"}
                            width={'100%'}
                            onChange={(event) => handleChange(+event.target.value, 'weightLimitPerParcel')}
                            error={!!errors['weightLimitPerParcel']}
                            errorMessage={errors['recipient'] && getMessage(errors['weightLimitPerParcel'], MessageSections.USER, messageCache, languageId)} />
                    </Grid>

                    <Grid item xs={4}>
                        <BarekamInput value={monthlyLimit.priceLimitPerParcel}
                            placeholder={`${getMessage('price', MessageSections.USER, messageCache, languageId)} ( ${currencies[1].name[languageId]} )`}
                            label={getMessage('price_euro', MessageSections.USER, messageCache, languageId)}
                            type={"number"}
                            customInputVariant={"standard"}
                            width={'100%'}
                            onChange={(event) => handleChange(+event.target.value, 'priceLimitPerParcel')}
                            error={!!errors['priceLimitPerParcel']}
                            errorMessage={errors['recipient'] && getMessage(errors['priceLimitPerParcel'], MessageSections.USER, messageCache, languageId)} />
                    </Grid>

                    {/*DISABLED BECAUSE ORDERS HAVE ONLY 1 PRODUCT*/}
                    {/*<Grid item xs={4}>*/}
                    {/*    <BarekamInput value={monthlyLimit.pieceCountLimitPerRecipient}*/}
                    {/*                  placeholder={getMessage('orders_count', MessageSections.USER, messageCache, languageId)}*/}
                    {/*                  label={getMessage('orders_count', MessageSections.USER, messageCache, languageId)}*/}
                    {/*                  type={"number"}*/}
                    {/*                  customInputVariant={"standard"}*/}
                    {/*                  width={'100%'}*/}
                    {/*                  onChange={(event) => handleChange(+event.target.value, 'pieceCountLimitPerRecipient')}*/}
                    {/*                  error={!!errors['pieceCountLimitPerRecipient']}*/}
                    {/*                  errorMessage={errors['recipient'] && getMessage(errors['pieceCountLimitPerRecipient'], MessageSections.USER, messageCache, languageId)}/>*/}
                    {/*</Grid>*/}
                </Grid>
            </DialogContent>

            <StandardDialogFooter saveLabel={<Message messageKey={'save'} sectionId={MessageSections.USER} />}
                onSave={() => save()}
                onCancel={() => popupClosed && popupClosed()} />
        </>
    )
}

interface EditRecipientLimitsProps extends PaperProps {
    recipientToEdit: Recipient,
    limitSaved: (recipient: Recipient, monthlyLimitData: MonthlyLimit) => void;
    popupClosed?: () => void;
}